import { useEffect, useState } from 'react';
import {
  fetchPublicBestByUnitType,
  fetchPublicProjectDetails,
  fetchPublicSitePlan,
  fetchPublicStackSoldPercentages,
  fetchPublicTowerViewUnits,
  PublicBestByUnitTypePayload,
  PublicProjectDetails,
  PublicSitePlanPayload,
  PublicTowerViewUnitsPayload,
} from '../services/superPickerPublicApi';

/**
 * Aggregated live data for the public project-detail page. Each field is
 * loaded independently and may be `null` if its endpoint failed/404'd; the
 * UI is expected to fall back to mock data in that case.
 */
export interface PublicProjectDetailData {
  details: PublicProjectDetails | null;
  sitePlan: PublicSitePlanPayload | null;
  stackSoldPercentages: Record<string, number> | null;
  towerView: PublicTowerViewUnitsPayload | null;
  bestByPrice: PublicBestByUnitTypePayload | null;
  bestByPsf: PublicBestByUnitTypePayload | null;
}

interface UseProjectDetailState extends PublicProjectDetailData {
  loading: boolean;
  error: string | null;
}

/**
 * Loads the full project-detail bundle (details + site plan + heat map
 * percentages + tower view) from the public API. Each request is independent
 * so a single endpoint failing won't blank out the whole page.
 */
export function useProjectDetail(projectId: string | null): UseProjectDetailState {
  const [state, setState] = useState<UseProjectDetailState>({
    loading: !!projectId,
    error: null,
    details: null,
    sitePlan: null,
    stackSoldPercentages: null,
    towerView: null,
    bestByPrice: null,
    bestByPsf: null,
  });

  useEffect(() => {
    if (!projectId) {
      setState({
        loading: false,
        error: null,
        details: null,
        sitePlan: null,
        stackSoldPercentages: null,
        towerView: null,
        bestByPrice: null,
        bestByPsf: null,
      });
      return;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    Promise.allSettled([
      fetchPublicProjectDetails(projectId),
      fetchPublicSitePlan(projectId),
      fetchPublicStackSoldPercentages(projectId),
      fetchPublicTowerViewUnits(projectId),
      fetchPublicBestByUnitType(projectId, 'price'),
      fetchPublicBestByUnitType(projectId, 'psf'),
    ]).then((results) => {
      if (cancelled) return;
      const [detailsR, sitePlanR, heatR, towerR, bestPriceR, bestPsfR] = results;
      const failures = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[];
      setState({
        loading: false,
        error: failures.length === results.length
          ? (failures[0]?.reason instanceof Error
              ? failures[0]?.reason.message
              : 'Failed to load project')
          : null,
        details: detailsR.status === 'fulfilled' ? detailsR.value : null,
        sitePlan: sitePlanR.status === 'fulfilled' ? sitePlanR.value : null,
        stackSoldPercentages: heatR.status === 'fulfilled' ? heatR.value : null,
        towerView: towerR.status === 'fulfilled' ? towerR.value : null,
        bestByPrice: bestPriceR.status === 'fulfilled' ? bestPriceR.value : null,
        bestByPsf: bestPsfR.status === 'fulfilled' ? bestPsfR.value : null,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return state;
}
